import { sanitizeFreeText } from '@tempo-io/string-util';
import type { LensFraming } from './framing-dto';

// The eight common titles are a fast path shared by the onboarding chips and SIR's own
// framing lookup: a hand-written framing per title, so a chip resolves with no model call anywhere.
export interface CommonTitle {
  title: string;
  framing: LensFraming;
}

const framing = (
  lensPhrase: string,
  emphasis: string,
  metricsToForeground: LensFraming['metricsToForeground'],
  detailLevel: LensFraming['detailLevel'],
  tone: string,
): LensFraming => ({ lensPhrase, emphasis, metricsToForeground, detailLevel, tone });

export const COMMON_TITLES: readonly CommonTitle[] = [
  {
    title: 'Product Leader',
    framing: framing(
      'Roadmap outcomes & scope health',
      'roadmap outcomes and scope health',
      ['scope drift', 'dependency risk', 'throughput'],
      'summary',
      'outcome-led',
    ),
  },
  {
    title: 'Product Member',
    framing: framing(
      'Scope health & delivery detail',
      'the scope and sequencing of the work in front of you',
      ['scope drift', 'blocked work', 'review latency'],
      'detailed',
      'practical',
    ),
  },
  {
    title: 'Engineering Leader',
    framing: framing(
      'Delivery risk & engineering capacity',
      'delivery risk and engineering capacity',
      ['blocked work', 'review latency', 'sprint carryover'],
      'summary',
      'practical',
    ),
  },
  {
    title: 'Engineering Member',
    framing: framing(
      'Blockers & review flow',
      'what is blocking the work you are closest to',
      ['blocked work', 'review latency', 'rework'],
      'detailed',
      'direct',
    ),
  },
  {
    title: 'PMO Leader',
    framing: framing(
      'Portfolio coverage & plan integrity',
      'plan integrity across the portfolio',
      ['unplanned scope', 'missing estimates', 'capacity coverage'],
      'summary',
      'measured',
    ),
  },
  {
    title: 'PMO Member',
    framing: framing(
      'Plan integrity & tracking hygiene',
      'the accuracy of the plan and its tracking',
      ['missing estimates', 'unplanned scope', 'stale items'],
      'detailed',
      'measured',
    ),
  },
  {
    title: 'Marketing Leader',
    framing: framing(
      'Launch readiness & dependency risk',
      'launch readiness and what could move a date',
      ['objectives at risk', 'dependency risk', 'delivery confidence'],
      'summary',
      'outcome-led',
    ),
  },
  {
    title: 'Organizational Leader',
    framing: framing(
      'Personalized to how you work',
      'portfolio outcomes and strategic risk',
      ['objectives at risk', 'delivery confidence', 'capacity coverage'],
      'summary',
      'strategic',
    ),
  },
] as const;

export const commonTitleFraming = (title: string): LensFraming | undefined => {
  const target = sanitizeFreeText(title).toLowerCase();

  return COMMON_TITLES.find((common) => common.title.toLowerCase() === target)?.framing;
};
